import '../styles/DragHandle.css';
import { isElementDomNode } from 'remirror';
import { NodeSelection, Plugin } from '@remirror/pm/state';
import { __serializeForClipboard } from '@remirror/pm/view';
import { nodeAtCoords, nodePosAtDOM } from '../utils';

export const OPEN_SHORTCUT_MENU_EVENT = 'open-shortcut-menu';
export const OPEN_SHORTCUT_MENU_EVENT_WITH_CONTEXT = 'open-shortcut-menu-context';
export const CLOSE_SHORTCUT_MENU_EVENT = 'close-shortcut-menu';

export function dragHandle(options = {}) {
  let handleWrapper = null;
  let dragHandleElement = null;
  let addButtonElement = null;
  let currentNode = null;
  let currentView = null;
  let isScrolling = false;
  let hideTimeout = null;
  let mouseMoveTimeout = null;

  const showHandles = () => {
    if (!isScrolling && handleWrapper) {
      handleWrapper.classList.add('visible');
      handleWrapper.classList.remove('hidden');
    }
  };

  const hideHandles = () => {
    handleWrapper.classList.add('hidden');
    handleWrapper.classList.remove('visible');
  };

  const handleScroll = () => {
    isScrolling = true;
    hideHandles();

    clearTimeout(window.dragHandleTimeout);
    window.dragHandleTimeout = setTimeout(() => {
      isScrolling = false;
    }, 250);
  };

  const handleDragStart = (event, view) => {
    if (!event.dataTransfer) return;

    const coords = { x: event.clientX + 50, y: event.clientY };
    const node = nodeAtCoords(coords, options.isDraggableBlock, 'drag');
    const pos = nodePosAtDOM(node, view);

    if (pos !== null) {
      view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));

      const slice = view.state.selection.content();
      const { dom, text } = __serializeForClipboard(view, slice);

      event.dataTransfer.clearData();
      event.dataTransfer.setData('text/html', dom.innerHTML);
      event.dataTransfer.setData('text/plain', text);

      const draggedElement = view.nodeDOM(pos);
      if (draggedElement) {
        event.dataTransfer.setDragImage(draggedElement, 0, 0);
      }

      view.dom.classList.add('dragging');
      view.dragging = { slice, move: true };
    }
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (currentNode && currentView) {
      const pos = nodePosAtDOM(currentNode, currentView);
      if (pos !== null) {
        currentView.dispatch(currentView.state.tr.setSelection(NodeSelection.create(currentView.state.doc, pos)));
        currentView.focus();

        const node = currentView.state.doc.nodeAt(pos);
        const toPos = pos + (node ? node.nodeSize : 1);

        const customEvent = new CustomEvent(OPEN_SHORTCUT_MENU_EVENT_WITH_CONTEXT, {
          detail: {
            view: currentView,
            position: {
              from: pos,
              to: toPos,
            },
          },
          bubbles: true,
        });
        document.dispatchEvent(customEvent);
      }
    }
  };

  const handleAddButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (currentNode && currentView) {
      const pos = nodePosAtDOM(currentNode, currentView);
      if (pos !== null) {
        const nodeSize = currentNode.nodeType === 1 ? currentView.state.doc.nodeAt(pos)?.nodeSize || 0 : 0;

        const insertPos = pos + nodeSize;
        const tr = currentView.state.tr;

        tr.insert(insertPos, currentView.state.schema.nodes.paragraph.create());

        currentView.dispatch(tr);

        const newCursorPos = insertPos + 1;
        const selectTr = currentView.state.tr.setSelection(
          currentView.state.selection.constructor.near(currentView.state.doc.resolve(newCursorPos))
        );

        currentView.dispatch(selectTr);
        currentView.focus();

        const customEvent = new CustomEvent(OPEN_SHORTCUT_MENU_EVENT, {
          detail: {
            position: newCursorPos,
          },
          bubbles: true,
        });

        document.dispatchEvent(customEvent);
      }
    }
  };

  const handleMouseMove = (view, event) => {
    if (!view.editable || isScrolling) return;

    clearTimeout(mouseMoveTimeout);
    mouseMoveTimeout = setTimeout(() => {
      const coords = {
        x: event.clientX + 50 + options.handlesWidth,
        y: event.clientY,
      };

      const node = nodeAtCoords(coords, options.isDraggableBlock, 'drag');
      if (!node || !isElementDomNode(node)) {
        hideHandles();
        return;
      }

      currentNode = node;
      currentView = view;

      const compStyle = window.getComputedStyle(node);
      const lineHeight = parseInt(compStyle.lineHeight, 10);
      const win = node.ownerDocument.defaultView;
      const boundingRect = node.getBoundingClientRect();
      let nestedNodeLeftOffset = 0;

      if (node.parentNode && !node.parentNode.classList.contains('ProseMirror')) {
        const parentCompStyle = window.getComputedStyle(node.parentNode);
        nestedNodeLeftOffset += parseFloat(parentCompStyle.paddingLeft, 10) || 0;
      }

      const nodePaddingTop = parseInt(compStyle.paddingTop, 10);
      const topPos = boundingRect.top + win.pageYOffset + (lineHeight - 24) / 2 + nodePaddingTop;
      const leftPos = boundingRect.left + win.pageXOffset - options.handlesWidth - nestedNodeLeftOffset;

      handleWrapper.style.left = `${leftPos}px`;
      handleWrapper.style.top = `${topPos}px`;

      showHandles();
    }, 50); // Small delay to prevent flickering
  };

  const handleMouseLeave = () => {
    hideTimeout = setTimeout(hideHandles, 300); // Hide after 300ms if no re-entry
  };

  const handleMouseEnter = () => {
    clearTimeout(hideTimeout);
    showHandles();
  };

  const handleOutsideClick = (event) => {
    event.stopPropagation();

    const customEvent = new CustomEvent(CLOSE_SHORTCUT_MENU_EVENT, {
      bubbles: true,
    });
    document.dispatchEvent(customEvent);
  };

  return new Plugin({
    props: {
      handleDOMEvents: {
        mousemove: (view, event) => handleMouseMove(view, event),
        mouseleave: () => handleMouseLeave(),
        keydown: () => hideHandles(),
      },
    },
    view: (editorView) => {
      handleWrapper = document.createElement('div');
      handleWrapper.draggable = 'true';
      handleWrapper.classList.add('handle-wrapper');
      hideHandles(); // Ensure hidden on init

      dragHandleElement = document.createElement('div');
      dragHandleElement.draggable = 'true';
      dragHandleElement.classList.add('drag-handle');
      dragHandleElement.title = 'Drag to move';

      addButtonElement = document.createElement('div');
      addButtonElement.classList.add('add-button');
      addButtonElement.title = 'Add new block';

      handleWrapper.appendChild(addButtonElement);
      handleWrapper.appendChild(dragHandleElement);

      dragHandleElement.addEventListener('dragstart', (e) => {
        handleDragStart(e, editorView);
      });

      dragHandleElement.addEventListener('click', handleClick);
      addButtonElement.addEventListener('click', handleAddButtonClick);

      handleWrapper.addEventListener('mouseenter', handleMouseEnter);
      handleWrapper.addEventListener('mouseleave', handleMouseLeave);

      document.body.appendChild(handleWrapper);
      const scrollableContainer = editorView.dom.closest('.space-editor'); // Adjust the selector as needed
      if (scrollableContainer) {
        scrollableContainer.addEventListener('scroll', handleScroll, { passive: true });
      }

      document.addEventListener('click', handleOutsideClick);
      return {
        destroy: () => {
          if (handleWrapper) {
            dragHandleElement.removeEventListener('dragstart', handleDragStart);
            dragHandleElement.removeEventListener('click', handleClick);
            addButtonElement.removeEventListener('click', handleAddButtonClick);
            handleWrapper.removeEventListener('mouseenter', handleMouseEnter);
            handleWrapper.removeEventListener('mouseleave', handleMouseLeave);
            handleWrapper.remove();
          }
          if (scrollableContainer) {
            scrollableContainer.removeEventListener('scroll', handleScroll);
          }
          handleWrapper = null;
          dragHandleElement = null;
          addButtonElement = null;
          currentNode = null;
          currentView = null;
          document.removeEventListener('click', handleOutsideClick);
        },
      };
    },
  });
}
